'use client';

import React, { useState } from 'react';
import { Loader2, MoreHorizontal, Trash, Star } from 'lucide-react';
import { AdvancedImage } from '@cloudinary/react';
import { LiveMessage } from '../../data/liveMessageData/liveMessage';
import { useGlobalChatState } from '../../hooks/useGlobalChatState';
import { ChatStatus } from '../../data/globalChatState';
import { useTranslations } from 'next-intl';

interface ImageGalleryGridProps {
  images: LiveMessage[];
  imagesPerRow?: number;
  onImageClick?: (message: LiveMessage) => void;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  onSetDefaultImage?: (messageId: string) => Promise<void>;
}

export default function ImageGalleryGrid({ 
  images, 
  imagesPerRow = 3,
  onImageClick,
  onDeleteMessage,
  onSetDefaultImage 
}: ImageGalleryGridProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const t = useTranslations('chat');
  
  // Get global chat state to check if we're in LIVE mode
  const [globalChatState] = useGlobalChatState();

  const handleDeleteMessage = async (e: React.MouseEvent, messageId: string) => {
    e.stopPropagation();
    if (onDeleteMessage) {
      try {
        await onDeleteMessage(messageId);
        setOpenMenuId(null);
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const handleSetDefaultImage = async (e: React.MouseEvent, messageId: string) => {
    e.stopPropagation();
    if (onSetDefaultImage) {
      try {
        await onSetDefaultImage(messageId);
        setOpenMenuId(null);
      } catch (error) {
        console.error('Error setting image as default:', error);
      }
    }
  };

  const toggleMenu = (e: React.MouseEvent, messageId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === messageId ? null : messageId);
  };

  if (!images || images.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-white/60 text-sm">
        {t('noImagesAvailable')}
      </div>
    );
  }

  return (
    <div className="grid gap-3 p-4 justify-items-start" style={{ gridTemplateColumns: `repeat(2, 1fr)` }}>
      {images.map((message) => {
        const isMsgAssistantImage = message.role === 'assistant' && message.imageUrl;
        
        if (!isMsgAssistantImage) return null;

        return (
          <div key={message.id} className="relative group">
            {/* More button - positioned on the top right, always visible in LIVE mode when image is loaded */}
            {true && 
             !message.isStreaming &&
             message.imageWithStatus.loadingStatus === 'loaded' && (
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={(e) => toggleMenu(e, message.id)}
                  className="text-white hover:text-gray-300 transition-colors duration-200 bg-black/60 backdrop-blur-sm rounded-full p-2 shadow-lg"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>

                {/* Dropdown Menu */}
                {openMenuId === message.id && (
                  <div className="absolute top-10 right-0 bg-[#1a1a1a] rounded-xl shadow-2xl z-[999999] w-auto min-w-[160px] whitespace-nowrap">
                    <button
                      onClick={(e) => handleSetDefaultImage(e, message.id)}
                      className={`w-full flex items-center justify-start px-4 py-2 text-white hover:bg-[#ffffff1a] transition-colors text-sm ${
                        message.id.startsWith('default') ? 'rounded-xl' : 'rounded-t-xl'
                      }`}
                    >
                      <Star className="w-4 h-4 mr-2" />
                      {t('setAsBackground')}
                    </button>
                    {!message.id.startsWith('default') && (
                      <button
                        onClick={(e) => handleDeleteMessage(e, message.id)}
                        className="w-full flex items-center justify-start px-4 py-2 text-red-400 hover:bg-[#ffffff1a] hover:text-red-300 transition-colors rounded-b-xl text-sm"
                      >
                        <Trash className="w-4 h-4 mr-2" />
                        {t('delete')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Image status text for assistant messages with images - only show during loading */}
            {message.imageWithStatus.loadingStatus === 'loading' && (
              <div className="aspect-square flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-white/60" />
              </div>
            )}
            
            {/* Show Cloudinary image when loaded */}
            {message.imageWithStatus.loadingStatus === 'loaded' && message.imageWithStatus.imageData && (
              <div 
                className="cursor-pointer rounded-lg overflow-hidden"
                onClick={() => onImageClick?.(message)}
              >
                <AdvancedImage
                  cldImg={message.imageWithStatus.imageData}
                  alt={message.content || t('generatedImage')}
                  className="w-full h-auto shadow-sm"
                  style={{ 
                    objectFit: 'cover',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                  loading="lazy"
                />
              </div>
            )}
            
            {/* Show "Image failed to load" message when image status is failed */}
            {message.imageWithStatus.loadingStatus === 'failed' && (
              <div className="aspect-square flex items-center justify-center">
                <div className="text-white/40 text-xs">×</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}